export type StagedRowSnapshot = Readonly<Record<string, unknown>>;

export type StagedValueStore = {
  subscribeStagedRow: (rowId: string, listener: () => void) => () => void;
  subscribeStagedValue: (rowId: string, columnId: string, listener: () => void) => () => void;
  getStagedValue: (rowId: string, columnId: string) => unknown;
  setStagedValue: (rowId: string, columnId: string, value: unknown) => void;
  clearStagedValue: (rowId: string, columnId: string) => void;
  reconcile: (read: (rowId: string, columnId: string) => unknown) => void;
  getRowSnapshot: (rowId: string) => StagedRowSnapshot;
  resetRow: (rowId: string, initial?: Record<string, unknown>) => void;
  resetAll: () => void;
};

export function createStagedValueStore(): StagedValueStore {
  const values = new Map<string, Map<string, unknown>>();
  const cellListeners = new Map<string, Map<string, Set<() => void>>>();
  const rowListeners = new Map<string, Set<() => void>>();
  const rowSnapshots = new Map<string, StagedRowSnapshot>();
  const emptySnapshot = Object.freeze({});

  function notifyCell(rowId: string, columnId: string) {
    const rowCellListeners = cellListeners.get(rowId);
    const columnListeners = rowCellListeners?.get(columnId);
    if (!columnListeners) return;
    for (const listener of columnListeners) {
      listener();
    }
  }

  function notifyRow(rowId: string) {
    const listeners = rowListeners.get(rowId);
    if (!listeners) return;
    for (const listener of listeners) {
      listener();
    }
  }

  function notifyChangedRow(rowId: string) {
    rowSnapshots.delete(rowId);
    notifyRow(rowId);
  }

  function getOrCreateRowValues(rowId: string) {
    let rowValues = values.get(rowId);
    if (!rowValues) {
      rowValues = new Map();
      values.set(rowId, rowValues);
    }
    return rowValues;
  }

  function notifyChangedRowValues(
    rowId: string,
    previousValues: Map<string, unknown> | undefined,
    nextValues: Map<string, unknown> | undefined,
  ) {
    const changedColumnIds = new Set([
      ...(previousValues?.keys() ?? []),
      ...(nextValues?.keys() ?? []),
    ]);
    let hasChangedValues = false;

    for (const columnId of changedColumnIds) {
      if (!Object.is(previousValues?.get(columnId), nextValues?.get(columnId))) {
        hasChangedValues = true;
        notifyCell(rowId, columnId);
      }
    }

    if (hasChangedValues) {
      notifyChangedRow(rowId);
    }
  }

  function clearStagedValue(rowId: string, columnId: string) {
    const rowValues = values.get(rowId);
    if (!rowValues?.has(columnId)) return;
    rowValues.delete(columnId);
    if (rowValues.size === 0) {
      values.delete(rowId);
    }
    notifyCell(rowId, columnId);
    notifyChangedRow(rowId);
  }

  return {
    subscribeStagedRow(rowId, listener) {
      let listeners = rowListeners.get(rowId);
      if (!listeners) {
        listeners = new Set();
        rowListeners.set(rowId, listeners);
      }
      listeners.add(listener);
      return () => {
        listeners!.delete(listener);
        if (listeners!.size === 0) {
          rowListeners.delete(rowId);
        }
      };
    },
    subscribeStagedValue(rowId, columnId, listener) {
      let rowCellListeners = cellListeners.get(rowId);
      if (!rowCellListeners) {
        rowCellListeners = new Map();
        cellListeners.set(rowId, rowCellListeners);
      }
      let columnListeners = rowCellListeners.get(columnId);
      if (!columnListeners) {
        columnListeners = new Set();
        rowCellListeners.set(columnId, columnListeners);
      }
      const cellListenerSet = columnListeners;
      cellListenerSet.add(listener);
      return () => {
        cellListenerSet.delete(listener);
        if (cellListenerSet.size === 0) {
          rowCellListeners!.delete(columnId);
          if (rowCellListeners!.size === 0) {
            cellListeners.delete(rowId);
          }
        }
      };
    },
    getStagedValue(rowId, columnId) {
      return values.get(rowId)?.get(columnId);
    },
    setStagedValue(rowId, columnId, value) {
      const rowValues = getOrCreateRowValues(rowId);
      if (rowValues.has(columnId) && Object.is(rowValues.get(columnId), value)) {
        return;
      }
      rowValues.set(columnId, value);
      notifyCell(rowId, columnId);
      notifyChangedRow(rowId);
    },
    clearStagedValue,
    reconcile(read) {
      const clearedCells: Array<[string, string]> = [];
      const changedRows = new Set<string>();

      for (const [rowId, rowValues] of values) {
        for (const [columnId, stagedValue] of rowValues) {
          if (Object.is(stagedValue, read(rowId, columnId))) {
            rowValues.delete(columnId);
            clearedCells.push([rowId, columnId]);
            changedRows.add(rowId);
          }
        }

        if (rowValues.size === 0) {
          values.delete(rowId);
        }
      }

      for (const [rowId, columnId] of clearedCells) {
        notifyCell(rowId, columnId);
      }
      for (const rowId of changedRows) {
        notifyChangedRow(rowId);
      }
    },
    getRowSnapshot(rowId) {
      const rowValues = values.get(rowId);
      if (!rowValues) return emptySnapshot;
      let snapshot = rowSnapshots.get(rowId);
      if (!snapshot) {
        snapshot = Object.freeze(Object.fromEntries(rowValues));
        rowSnapshots.set(rowId, snapshot);
      }
      return snapshot;
    },
    resetRow(rowId, initial = {}) {
      const previousValues = values.get(rowId);
      const nextValues = new Map(Object.entries(initial));
      if (nextValues.size > 0) {
        values.set(rowId, nextValues);
      } else {
        values.delete(rowId);
      }
      notifyChangedRowValues(rowId, previousValues, nextValues.size > 0 ? nextValues : undefined);
    },
    resetAll() {
      const previousValuesByRow = new Map(values);
      values.clear();
      for (const [rowId, previousValues] of previousValuesByRow) {
        notifyChangedRowValues(rowId, previousValues, undefined);
      }
    },
  };
}
